import { Modal } from 'antd';
import chatApi from 'api/chatApi';
import userApi from 'api/userApi';
import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import EmptyTableIcon from 'assets/icons/EmptyTableIcon';
import tbet_token_logo from 'assets/images/terabet_coin_logo.png';
import usd_logo from 'assets/images/usd-logo.png';
import ConditionButton from 'components/Commons/ConditionButton';
import { showSuccessModal } from 'components/Modals/CommonModals';
import { IMG_ALT } from 'consts/app';
import Typo from 'consts/typography';
import { setShowConfetti } from 'features/home/homeSlice';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useThemes from 'hooks/useThemes';
import { GiveAwayResponseModel } from 'models/chatMessage';
import { useEffect, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import SpinWheelPopup from './SpinWheelModal';

interface CurrentGiveAwayModalProps {
  visible: boolean;
  closeModal: () => void;
  className?: string;
}

const CurrentGiveAwayModal: React.FunctionComponent<CurrentGiveAwayModalProps> = (props) => {
  const { visible, closeModal } = props;
  const { currentTheme } = useThemes();
  const t = useAppTranslation();
  const handleResponseError = useHandleResponseError();
  const platform = useAppSelector(platformStates);
  const { isDesktop, isLargeDesktop } = platform;
  const dispatch = useAppDispatch();
  const [giveaways, setGiveaways] = useState<GiveAwayResponseModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSpinWheel, setShowSpinWheel] = useState<boolean>(false);
  const [selectedGiveaway, setSelectedGiveaway] = useState<GiveAwayResponseModel | null>(null);

  const getGiveaways = async () => {
    const { ok, body } = await chatApi.getGiveAwayList();
    if (ok && body) {
      setGiveaways(body);
    }
  };

  useEffect(() => {
    if (visible) getGiveaways();
  }, [visible]);

  const handleClaimGiveAway = async (giveaway: GiveAwayResponseModel) => {
    if (giveaway.promotionType === 'SPIN') {
      setSelectedGiveaway(giveaway);
      setShowSpinWheel(true);
      return;
    }

    setIsLoading(true);
    const { ok, body, error } = await userApi.usePromotion({
      promotionCode: giveaway.promotionCode,
    });
    setIsLoading(false);
    getGiveaways();
    if (ok && body) {
      dispatch(setShowConfetti(true));
      showSuccessModal({
        title: t('event.claim.success.title'),
        content: t('event.claim.success.message'),
        onOk: () => {},
      });
    }
    if (error) {
      handleResponseError({
        ...error,
        message:
          error.message === 'error.message.user.block-chat'
            ? 'error.message.user.block-chat'
            : error.message === 'error.promotion.used'
            ? 'error.promotion.already-giveaway'
            : 'error.promotion.disable-giveaway',
      });
    }
  };

  const handleCloseSpinWheel = () => {
    setShowSpinWheel(false);
    setSelectedGiveaway(null);
    getGiveaways();
  };

  return (
    <>
      <Modal
        className="common-modal user-profile-modal"
        title={t('give-away.modal.title')}
        visible={visible}
        onCancel={closeModal}
        footer={null}
        centered
        maskClosable={false}
        closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
        width={600}
      >
        <div className="giveaway-container">
          {giveaways.length > 0 ? (
            giveaways
              .sort((a, b) => Number(a.isUsed) - Number(b.isUsed))
              .map((giveaway) => (
                <div className="giveaway-item" key={giveaway.promotionCode}>
                  <div className="give_away_content">
                    {giveaway.coinSymbol === 'USD' && <img src={usd_logo} alt={IMG_ALT} />}
                    {giveaway.coinSymbol === '$TBET' && <img src={tbet_token_logo} alt={IMG_ALT} />}
                    {giveaway.promotionType === 'GIVE_AWAY' ? (
                      <>
                        <p className="mb-0">
                          {giveaway.amount} {giveaway.coinSymbol}
                        </p>
                        <span className="font-bold">
                          {!!giveaway.walletReceive ? giveaway.walletReceive : 'BET'}
                        </span>
                        <span className="font-bold">Instant Giveaway</span>
                        <div className="user_left-container">
                          <span>
                            {giveaway.remainQuantity} / {giveaway.totalQuantity}
                          </span>
                          <br />
                          <span className="user_left">{t('give-away.modal.user-left')}</span>
                        </div>
                      </>
                    ) : (
                      <div className="spin-content">
                        <div className="spin-icon">🎡</div>
                        <span className="spin-text">Spin</span>
                      </div>
                    )}

                    <ConditionButton
                      onClick={() => handleClaimGiveAway(giveaway)}
                      className={`follow-bet__btn ${
                        isDesktop || isLargeDesktop ? Typo.p4 : Typo.p6
                      } ${giveaway.isUsed ? 'disable_btn' : ''}`}
                      content={
                        giveaway.isUsed ? t('give-away.modal.used') : t('chat.give-away.get-reward')
                      }
                      testerCheck={true}
                      disabled={isLoading || giveaway.isUsed}
                    />
                  </div>
                </div>
              ))
          ) : (
            <div className="combo-bet-empty">
              <EmptyTableIcon />
            </div>
          )}
        </div>
      </Modal>
      {showSpinWheel && selectedGiveaway && (
        <SpinWheelPopup
          isOpen={showSpinWheel}
          onClose={handleCloseSpinWheel}
          giveAwayData={selectedGiveaway}
        />
      )}
    </>
  );
};

export default CurrentGiveAwayModal;
